import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// ─── Design tokens ────────────────────────────────────────────────────────────
const PURPLE_GRADIENT = "linear-gradient(2deg, #A28FDC 0.74%, #7C3AED 74.61%)";
const PURPLE_SHADOW   = "0 10px 20px 0 rgba(91,76,219,0.30)";
const PURPLE_LIGHT_BG = "#EDE8FC";  // active date-card fill
const PURPLE_SOLID    = "#7C3AED";

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
const LocationPinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M14.5533 14.106C14.7723 13.9964 15.0239 13.971 15.2604 14.0346C15.4969 14.0982 15.7018 14.2464 15.8363 14.451L15.8943 14.553L17.8943 18.553C17.9657 18.6956 18.0019 18.8533 17.9999 19.0128C17.9979 19.1723 17.9578 19.329 17.883 19.4698C17.8081 19.6106 17.7006 19.7315 17.5695 19.8224C17.4384 19.9132 17.2875 19.9714 17.1293 19.992L17.0003 20H1.00032C0.840792 20.0001 0.683565 19.9619 0.541769 19.8888C0.399974 19.8157 0.277725 19.7098 0.185231 19.5798C0.0927381 19.4498 0.0326845 19.2996 0.0100851 19.1417C-0.0125143 18.9837 0.00299652 18.8227 0.0553224 18.672L0.105322 18.552L2.10532 14.552C2.21742 14.3203 2.41455 14.1407 2.65568 14.0507C2.89681 13.9606 3.16338 13.967 3.39991 14.0686C3.63644 14.1701 3.82472 14.3589 3.92557 14.5957C4.02641 14.8325 4.03205 15.0991 3.94132 15.34L3.89432 15.447L2.61832 18H15.3823L14.1063 15.447C13.9879 15.2099 13.9685 14.9354 14.0523 14.684C14.1361 14.4325 14.3163 14.2246 14.5533 14.106ZM9.00032 0C10.8568 0 12.6373 0.737498 13.9501 2.05025C15.2628 3.36301 16.0003 5.14348 16.0003 7C16.0003 9.382 14.7113 11.317 13.3773 12.69C12.6405 13.4385 11.8305 14.1112 10.9593 14.698L10.5863 14.944L10.2543 15.153L10.1053 15.243L9.84832 15.391C9.32032 15.691 8.68032 15.691 8.15232 15.391L7.89532 15.242L7.58532 15.053L7.41432 14.944L7.04132 14.698C6.17018 14.1112 5.36013 13.4385 4.62332 12.69C3.28932 11.317 2.00032 9.382 2.00032 7C2.00032 5.14348 2.73782 3.36301 4.05057 2.05025C5.36333 0.737498 7.14381 0 9.00032 0ZM9.00032 2C7.67424 2 6.40247 2.52678 5.46479 3.46447C4.52711 4.40215 4.00032 5.67392 4.00032 7C4.00032 8.636 4.89032 10.095 6.05732 11.296C6.75929 12.005 7.53503 12.6369 8.37132 13.181L8.71132 13.398C8.81666 13.4627 8.91299 13.5207 9.00032 13.572L9.29032 13.398L9.62932 13.181C10.4656 12.6369 11.2414 12.005 11.9433 11.296C13.1103 10.096 14.0003 8.636 14.0003 7C14.0003 5.67392 13.4735 4.40215 12.5359 3.46447C11.5982 2.52678 10.3264 2 9.00032 2ZM9.00032 4C9.79597 4 10.559 4.31607 11.1216 4.87868C11.6843 5.44129 12.0003 6.20435 12.0003 7C12.0003 7.79565 11.6843 8.55871 11.1216 9.12132C10.559 9.68393 9.79597 10 9.00032 10C8.20467 10 7.44161 9.68393 6.879 9.12132C6.31639 8.55871 6.00032 7.79565 6.00032 7C6.00032 6.20435 6.31639 5.44129 6.879 4.87868C7.44161 4.31607 8.20467 4 9.00032 4ZM9.00032 6C8.73511 6 8.48075 6.10536 8.29322 6.29289C8.10568 6.48043 8.00032 6.73478 8.00032 7C8.00032 7.26522 8.10568 7.51957 8.29322 7.70711C8.48075 7.89464 8.73511 8 9.00032 8C9.26554 8 9.51989 7.89464 9.70743 7.70711C9.89497 7.51957 10.0003 7.26522 10.0003 7C10.0003 6.73478 9.89497 6.48043 9.70743 6.29289C9.51989 6.10536 9.26554 6 9.00032 6Z" fill="#5454D4"/>
  </svg>
);

const TriangleDown = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
    <path d="M1 1.5L6 6.5L11 1.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TriangleUp = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
    <path d="M11 6.5L6 1.5L1 6.5" stroke={PURPLE_SOLID} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = ({ color = "#2616A7" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clipPath="url(#clip0_309_3522)">
      <path d="M14.2501 8.74999C13.7501 11.25 11.8652 13.6036 9.2194 14.1299C6.57362 14.6562 3.88874 13.4255 2.56037 11.0776C1.232 8.72976 1.55994 5.79453 3.37373 3.79771C5.18752 1.80088 8.25009 1.25 10.7501 2.25" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.75 7.75L8.25 10.25L14.25 3.75" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_309_3522">
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const ShieldInfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1L1.75 3.25v3.5C1.75 9.975 4.025 12.95 7 13.75c2.975-.8 5.25-3.775 5.25-6.975v-3.5L7 1Z" stroke="#9CA3AF" strokeWidth="1.2" strokeLinejoin="round"/>
    <path d="M7 5.5v3M7 4.5h.007" stroke="#9CA3AF" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const CarSearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path fillRule="evenodd" clipRule="evenodd" d="M20.1585 2.49348C21.4538 2.49343 23.0121 2.34303 24.176 2.9466C24.7822 3.25894 25.2978 3.72227 25.6721 4.29231C25.9725 4.75521 26.1346 5.21972 26.305 5.73957C26.7333 7.0467 27.1872 8.34909 27.6048 9.6595C28.0478 9.39506 28.5317 9.0208 29.054 9.15754C29.321 9.23032 29.5476 9.40816 29.6809 9.65071C29.8083 9.8732 29.8413 10.138 29.7727 10.3851C29.6144 10.9427 28.7669 11.3079 28.2591 11.5911C28.3162 11.8047 28.3498 11.9082 28.4358 12.1116C29.4863 12.8115 30.1808 13.4733 30.6575 14.6771C31.0574 15.687 31.0071 16.6397 31.0003 17.6986C30.6442 17.1322 30.1192 16.5703 29.6155 16.1322C29.3721 15.9204 29.0794 15.7116 28.8528 15.4827C28.7897 15.4183 28.6542 15.1246 28.6038 15.0286C28.4794 14.7936 28.3196 14.5785 28.1301 14.3919C27.0372 13.2952 25.7279 13.5026 24.305 13.5023L6.76296 13.5013C5.6073 13.5024 4.74625 13.5165 3.86745 14.3958C2.97814 15.2858 3.00286 16.242 3.00222 17.4046L3.00417 19.0784L3.00319 22.0511C3.00908 22.7758 3.03623 23.3585 3.58522 23.9095C4.64318 24.9713 6.52001 24.5045 6.91433 23.0511C7.06674 22.4891 6.84194 21.7395 7.2903 21.2952C7.42668 21.1602 7.59764 21.0565 7.79323 21.0345C8.61913 20.9651 9.51015 20.9934 10.3372 20.9935H15.6253C15.5217 21.5212 15.5164 22.4582 15.5247 23.0013C13.3761 23.0374 11.1269 23.0024 8.96999 23.0023C8.95221 23.1192 8.93118 23.236 8.90749 23.3519C8.69422 24.368 8.04218 25.2956 7.17311 25.8597C6.26633 26.4368 5.16739 26.6318 4.11745 26.4017C3.10443 26.1819 2.18422 25.5246 1.62624 24.6536C1.34209 24.2132 1.15148 23.7187 1.06667 23.2015C0.948159 22.4526 0.99674 20.7788 0.996357 19.9261L0.994404 17.7093C0.99415 16.9932 0.967511 16.0532 1.1321 15.3685C1.49225 13.8703 2.28842 12.936 3.55104 12.1331C3.6147 12.0017 3.68308 11.7987 3.73561 11.6575C3.20738 11.3137 2.26349 10.9845 2.12233 10.3372C2.06545 10.0744 2.11505 9.79943 2.26101 9.57356C2.40478 9.3523 2.63405 9.20095 2.89382 9.15461C3.41838 9.05641 3.95245 9.4377 4.36843 9.72395L5.37136 6.71418C5.54974 6.17676 5.81855 5.28899 6.04518 4.80598C6.20078 4.47092 6.40178 4.15821 6.64284 3.87825C7.39957 3.00212 8.33943 2.59674 9.47585 2.51301C9.97744 2.46506 11.0742 2.49434 11.6018 2.49446L20.1585 2.49348ZM11.718 4.50227C11.1557 4.50218 10.2242 4.47382 9.68874 4.51301C8.02371 4.62129 7.83095 5.65048 7.37917 7.02864C7.3135 7.22892 7.24287 7.43745 7.17604 7.63801L5.89186 11.4886C7.85915 11.5118 9.84864 11.4955 11.8186 11.4954L22.3596 11.4945C23.5868 11.4945 24.8888 11.5177 26.1096 11.4906L24.8391 7.67903C24.6041 6.97224 24.3577 6.08024 24.0149 5.43196C23.7083 4.85224 22.9545 4.49504 22.3216 4.51399C21.5314 4.4998 20.7461 4.50098 19.9563 4.50129L11.718 4.50227Z" fill="#EFEDFF"/>
    <path fillRule="evenodd" clipRule="evenodd" d="M23.6417 16.5137C24.843 16.3801 26.339 16.8642 27.3282 17.5469C28.6274 18.4399 29.5172 19.8136 29.8018 21.3643C30.0995 22.9889 29.7419 24.5466 28.8048 25.8994C29.1009 26.1422 30.6814 27.7207 30.8389 27.9668C30.9367 28.1201 30.9905 28.2978 30.9942 28.4795C31.0099 29.0551 30.569 29.4923 29.9971 29.4961C29.8208 29.4976 29.6473 29.449 29.4971 29.3565C29.229 29.1914 27.7944 27.7133 27.4757 27.3955L27.418 27.335C27.3734 27.3393 26.5324 27.8233 26.38 27.8906C25.7353 28.171 25.0479 28.3408 24.3468 28.3916C22.786 28.4971 21.2464 27.9826 20.0626 26.96C18.8801 25.9452 18.1483 24.5027 18.0274 22.9492C17.7844 19.5281 20.2241 16.7432 23.6417 16.5137ZM27.8653 21.9356C27.5795 19.7763 25.598 18.2568 23.4386 18.541C21.277 18.8257 19.756 20.8093 20.0421 22.9707C20.3282 25.1321 22.3126 26.6518 24.4737 26.3643C26.6328 26.077 28.1509 24.0948 27.8653 21.9356Z" fill="#EFEDFF"/>
    <path d="M7.41168 16.5099C8.01971 16.4759 9.63993 16.3784 10.106 16.7037C10.3136 16.8487 10.4433 17.0699 10.4842 17.3185C10.5294 17.5897 10.463 17.8677 10.3 18.0892C10.1044 18.3569 9.89931 18.4397 9.58943 18.4914C9.01109 18.528 7.3394 18.6056 6.89525 18.2961C6.68581 18.1501 6.55706 17.9295 6.51609 17.679C6.47153 17.4092 6.53709 17.1327 6.69812 16.9116C6.8944 16.6417 7.10118 16.5604 7.41168 16.5099Z" fill="#EFEDFF"/>
  </svg>
);

const DateCard = ({ label, name, date, time, done, onClick }) => (
  <button
    onClick={onClick}
    className="rounded-2xl text-left transition-all w-full overflow-hidden"
    style={{ background: "#EBF4FF", border: "1.2px solid #c5c5cc" }}
  >
    {/* Label row — only this row has side/top padding */}
    <div className="flex items-center justify-between px-3 pt-3 pb-2">
      <span style={{
        color: "#2616A7",
        fontFamily: "Outfit, sans-serif",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        lineHeight: "normal",
      }}>
        {label}
      </span>
      {done && <CheckIcon color="#2616A7" />}
    </div>
    {/* Inner white pill — flush to left, right and bottom edges */}
    <div className="bg-white rounded-t-xl px-4 py-3">
      <p className="text-[#1e1b4b] font-bold font-['Outfit'] text-[18px] leading-snug">
        {date ? date.format("D MMM YYYY") : `Select ${name} date`}
      </p>
      <p className="text-[#6b7280] text-[13px] font-['Outfit'] mt-0.5">
        {time ? dayjs(`2000-01-01 ${time}`).format("h:mm A") : `Select ${name} time`}
      </p>
    </div>
  </button>
);

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: "daily", label: "Daily Rentals" },
  { id: "7days", label: "7 Days +" },
  { id: "30days", label: "30 Days +" },
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildCalendarDays(month) {
  const start = month.startOf("month");
  const end   = month.endOf("month");
  const days  = [];
  for (let i = 0; i < start.day(); i++) days.push(null);
  for (let d = start; !d.isAfter(end); d = d.add(1, "day")) days.push(d.clone());
  return days;
}

function generateTimeSlots(hourlyOnly = false) {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m of (hourlyOnly ? [0] : [0, 30])) {
      const value = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
      slots.push({ value, label: dayjs(`2000-01-01 ${value}`).format("h:mm A") });
    }
  }
  return slots;
}

const DAILY_SLOTS = generateTimeSlots(false);
const SUB_SLOTS   = generateTimeSlots(true);

function roundToNearest30(t) {
  const mins = t.minute();
  if (mins < 15) return t.minute(0).second(0);
  if (mins < 45) return t.minute(30).second(0);
  return t.add(1, "hour").minute(0).second(0);
}

function roundUpToHour(t) {
  return t.minute() === 0 ? t.second(0) : t.add(60 - t.minute(), "minute").second(0);
}

const toDt = (d, t) => {
  const [h, m] = (t || "10:00").split(":").map(Number);
  return d.hour(h).minute(m).second(0).millisecond(0);
};

// ─── Component ────────────────────────────────────────────────────────────────
// When `onConfirm` is passed the parent owns the search (listing page): the overlay starts from
// `initialValues`, uses the parent's buffer/min-hours props as-is and hands the picked values back.
export default function BookingOverlay({ open, onClose, cities = [], initialTab = "daily", initialStep = null, minimal = false, onConfirm = null, initialValues = null, bufferTime: bufferTimeProp = 3, minBookingHrs: minBookingHrsProp = 18 }) {
  const navigate = useNavigate();
  const controlled = !!onConfirm;

  const [tab,           setTab]           = useState(initialTab);
  const [cityId,        setCityId]        = useState("");
  const [cityOpen,      setCityOpen]      = useState(false);
  const [hubs,          setHubs]          = useState([]);
  const [hubId,         setHubId]         = useState("");
  const [step,          setStep]          = useState(null); // null | "pickup-date" | "pickup-time" | "dropoff-date" | "dropoff-time"
  const [calMonth,      setCalMonth]      = useState(dayjs());
  const [pickupDate,    setPickupDate]    = useState(null);
  const [pickupTime,    setPickupTime]    = useState("");
  const [dropDate,      setDropDate]      = useState(null);
  const [dropTime,      setDropTime]      = useState("");
  const [minBookingHrs, setMinBookingHrs] = useState(minBookingHrsProp);
  const [bufferTime,    setBufferTime]    = useState(bufferTimeProp);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setCityOpen(false);
    setStep(initialStep || null);
    setMinBookingHrs(minBookingHrsProp);
    setBufferTime(bufferTimeProp);
    const iv = initialValues;
    if (iv?.pickupDate && iv?.pickupTime && iv?.dropDate && iv?.dropTime) {
      setPickupDate(iv.pickupDate);
      setPickupTime(iv.pickupTime);
      setDropDate(iv.dropDate);
      setDropTime(iv.dropTime);
      setCalMonth((initialStep === "dropoff-date" ? iv.dropDate : iv.pickupDate).startOf("month"));
      return;
    }
    const bufferStart = dayjs().add(bufferTimeProp ?? 3, "hour");
    const candidate = initialTab === "daily" ? roundToNearest30(bufferStart) : roundUpToHour(bufferStart);
    setPickupDate(candidate);
    setPickupTime(candidate.format("HH:mm"));
    const defaultDropDays = initialTab === "7days" ? 7 : initialTab === "30days" ? 30 : 0;
    const drop = defaultDropDays > 0
      ? candidate.add(defaultDropDays, "day")
      : candidate.add(minBookingHrsProp || 18, "hour");
    setDropDate(drop);
    setDropTime(drop.format("HH:mm"));
    setCalMonth(candidate.startOf("month"));
  }, [open]);

  useEffect(() => { if (cities.length && !cityId) setCityId(String(cities[0]?.id ?? "")); }, [cities]);
  useEffect(() => { setTab(initialTab); }, [initialTab]);

  useEffect(() => {
    if (!cityId) return;
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    fetch(`${apiUrl}/api/hubs?limit=100&cityId=${cityId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.statusCode === "0100" && data?.entities?.rows?.length) {
          setHubs(data.entities.rows);
          setHubId(String(data.entities.rows[0].id));
        } else {
          setHubs([]);
          setHubId("");
        }
      })
      .catch(() => { setHubs([]); setHubId(""); });
  }, [cityId]);

  // Parent-controlled: follow its rules (EV-aware, current city) instead of fetching our own
  useEffect(() => {
    if (!controlled) return;
    setBufferTime(bufferTimeProp);
    setMinBookingHrs(minBookingHrsProp);
  }, [bufferTimeProp, minBookingHrsProp]); // eslint-disable-line

  useEffect(() => {
    if (controlled || !cityId || tab !== "daily") return;
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    fetch(`${apiUrl}/api/bookingconfigs?cityId=${cityId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.statusCode === "0100") {
          const config = data.entities?.rows?.[0];
          if (config?.minBookingHrs) setMinBookingHrs(config.minBookingHrs);
          if (config?.bufferTime !== undefined) setBufferTime(config.bufferTime);
        }
      })
      .catch(() => {});
  }, [cityId, tab]);

  const selectedCity = cities.find((c) => String(c.id) === String(cityId));
  const isSubscription  = tab !== "daily";
  const subMinDays = tab === "7days" ? 7  : tab === "30days" ? 30 : 0;
  const subMaxDays = tab === "7days" ? 29 : tab === "30days" ? 90 : 0;

  const ALL_SLOTS = isSubscription ? SUB_SLOTS : DAILY_SLOTS;

  const getEarliest = () => {
    const t = dayjs().add(bufferTime ?? 3, "hour");
    return (isSubscription ? roundUpToHour(t) : roundToNearest30(t)).millisecond(0);
  };

  // Keeps a valid drop-off as is; only pushes it forward (or clamps a subscription to its day range)
  const fixDrop = (pickup, drop) => {
    if (isSubscription) {
      const d   = drop ? toDt(drop, pickup.format("HH:mm")) : null; // drop time mirrors pickup time
      const min = pickup.add(subMinDays, "day");
      const max = pickup.add(subMaxDays, "day");
      if (!d || d.isBefore(min)) return min;
      return d.isAfter(max) ? max : d;
    }
    const min = pickup.add(minBookingHrs ?? 18, "hour");
    return !drop || drop.isBefore(min) ? min : drop;
  };

  const currentDrop = () => (dropDate && dropTime ? toDt(dropDate, dropTime) : null);

  const commit = (pickup, drop) => {
    const next = { pickupDate: pickup, pickupTime: pickup.format("HH:mm"), dropDate: drop, dropTime: drop.format("HH:mm") };
    setPickupDate(next.pickupDate);
    setPickupTime(next.pickupTime);
    setDropDate(next.dropDate);
    setDropTime(next.dropTime);
    return next;
  };

  // Rules changed (city / EV): move only the invalid parts forward
  useEffect(() => {
    if (!open || !pickupDate || !pickupTime) return;
    const current = toDt(pickupDate, pickupTime);
    const earliest = getEarliest();
    const pickup = current.isBefore(earliest) ? earliest : current;
    const drop = fixDrop(pickup, isSubscription ? dropDate : currentDrop());
    const prevDrop = currentDrop();
    if (!pickup.isSame(current) || !prevDrop || !drop.isSame(prevDrop)) commit(pickup, drop);
  }, [minBookingHrs, bufferTime]); // eslint-disable-line

  const getAvailablePickupSlots = () => {
    if (!pickupDate) return ALL_SLOTS;
    // every date, not just today: a late-night buffer can push the earliest slot into tomorrow
    const earliest = getEarliest();
    return ALL_SLOTS.filter(({ value }) => !toDt(pickupDate, value).isBefore(earliest));
  };

  const getAvailableDropSlots = () => {
    if (!pickupDate || !pickupTime || !dropDate) return ALL_SLOTS;
    const [ph, pm] = pickupTime.split(":").map(Number);
    const minDrop  = pickupDate.hour(ph).minute(pm).add(minBookingHrs ?? 18, "hour");
    return ALL_SLOTS.filter(({ value }) => {
      const [dh, dm] = value.split(":").map(Number);
      return !dropDate.hour(dh).minute(dm).isBefore(minDrop);
    });
  };

  // kept for calendar disabled-day logic (no slot filtering needed there)
  const getDisabledPickup = () => new Set();
  const getDisabledDrop   = () => new Set();

  const getDuration = () => {
    if (!pickupDate || !pickupTime || !dropDate || !dropTime) return "";
    const [sh, sm] = pickupTime.split(":").map(Number);
    const [eh, em] = dropTime.split(":").map(Number);
    let diff = dropDate.hour(eh).minute(em).diff(pickupDate.hour(sh).minute(sm), "minute");
    if (diff <= 0) return "—";
    const days  = Math.floor(diff / 1440); diff -= days * 1440;
    const hours = Math.floor(diff / 60);
    const mins  = diff % 60;
    return [days && `${days}d`, hours && `${hours}h`, mins && `${mins}m`].filter(Boolean).join(" ");
  };

  const handleSearch = () => {
    if (!pickupDate || !pickupTime) { toast.error("Select a pickup date & time"); return; }
    if (!dropDate || !dropTime) { toast.error("Select a drop-off date & time"); return; }

    const formatpickupdate = pickupDate.format("YYYY-MM-DD");
    const formatpickuptime = pickupTime;

    if (tab === "daily") {
      const formatenddate = dropDate.format("YYYY-MM-DD");
      const formatdroptime = dropTime;
      if (moment(`${formatenddate} ${formatdroptime}`, "YYYY-MM-DD HH:mm").diff(moment(`${formatpickupdate} ${formatpickuptime}`, "YYYY-MM-DD HH:mm"), "hours", true) < minBookingHrs) {
        toast.error(`Minimum booking is ${minBookingHrs} hours`); return;
      }
      onClose();
      navigate(`/listings?${new URLSearchParams({ cityId, hubid: hubId, formatpickupdate, formatenddate, formatpickuptime, formatdroptime, vechicleTypeId: "Car" })}`);
    } else {
      const [ph, pm] = formatpickuptime.split(":").map(Number);
      const [dh, dm] = dropTime.split(":").map(Number);
      const startDt   = dayjs(formatpickupdate).hour(ph).minute(pm);
      const endDt     = dayjs(dropDate.format("YYYY-MM-DD")).hour(dh).minute(dm);
      const diffDays  = endDt.diff(startDt, "day", true);
      if (diffDays < subMinDays) { toast.error(`Minimum duration is ${subMinDays} days`); return; }
      if (diffDays > subMaxDays) { toast.error(`Maximum duration is ${subMaxDays} days`); return; }
      onClose();
      navigate(`/sublistings?${new URLSearchParams({ cityId, hubid: hubId, startDate: startDt.format("YYYY-MM-DDTHH:mm"), endDate: endDt.format("YYYY-MM-DDTHH:mm"), freeKmValue: "140" })}`);
    }
  };

  const disabledPickup  = getDisabledPickup();
  const disabledDrop    = getDisabledDrop();
  const availPickup     = getAvailablePickupSlots();
  const availDrop       = getAvailableDropSlots();
  const pickupDone      = !!(pickupDate && pickupTime);
  const dropDone        = !!(dropDate && dropTime);
  const calDays         = buildCalendarDays(calMonth);
  const isPickupStep    = step?.startsWith("pickup");
  const isDropoffStep   = step?.startsWith("dropoff");
  const isDateStep      = step?.endsWith("-date");
  const isTimeStep      = step?.endsWith("-time");
  const stepTitle       = isPickupStep ? "Pick-up" : "Drop-off";
  const stepLabel       = `Select ${isPickupStep ? "pickup" : "drop"} ${isDateStep ? "date" : "time"}`;

  // `next` carries values set in the same click — state hasn't re-rendered yet, so don't read it here
  const closeStep = (next = { pickupDate, pickupTime, dropDate, dropTime }) => {
    if (minimal && step === "pickup-time" && initialStep === "pickup-date") {
      // Auto-advance: after pickup time confirmed, go straight to drop date
      if (next.dropDate) setCalMonth(next.dropDate.startOf("month"));
      setStep("dropoff-date");
    } else if (minimal) {
      if (onConfirm) onConfirm(next);
      onClose();
    } else {
      setStep(null);
    }
  };

  const earliestPickup = getEarliest();
  const lastSlot = ALL_SLOTS[ALL_SLOTS.length - 1].value;

  const isDayDisabled = (day) => {
    // no slot left on this day once the buffer is applied
    if (toDt(day, lastSlot).isBefore(earliestPickup)) return true;
    if (!isDropoffStep || !pickupDate) return false;
    if (isSubscription) {
      return day.isBefore(pickupDate.add(subMinDays, "day"), "day") || day.isAfter(pickupDate.add(subMaxDays, "day"), "day");
    }
    // no slot on this day reaches the minimum booking duration
    return toDt(day, lastSlot).isBefore(toDt(pickupDate, pickupTime).add(minBookingHrs ?? 18, "hour"));
  };

  const handleDayClick = (day) => {
    if (isPickupStep) {
      const picked  = toDt(day, pickupTime);
      const pickup  = picked.isBefore(earliestPickup) ? earliestPickup : picked;
      commit(pickup, fixDrop(pickup, isSubscription ? dropDate : currentDrop()));
      setStep("pickup-time");
    } else if (isSubscription) {
      // drop time always mirrors pickup time — no drop time step
      closeStep(commit(toDt(pickupDate, pickupTime), toDt(day, pickupTime)));
    } else {
      const minDrop = toDt(pickupDate, pickupTime).add(minBookingHrs ?? 18, "hour");
      const picked  = toDt(day, dropTime);
      commit(toDt(pickupDate, pickupTime), picked.isBefore(minDrop) ? minDrop : picked);
      setStep("dropoff-time");
    }
  };

  const handleSlotClick = (value) => {
    if (isPickupStep) {
      const pickup = toDt(pickupDate, value);
      closeStep(commit(pickup, fixDrop(pickup, isSubscription ? dropDate : currentDrop())));
    } else {
      closeStep(commit(toDt(pickupDate, pickupTime), toDt(dropDate, value)));
    }
  };

  const openPanel = (side) => {
    if (side === "pickup"  && pickupDate) setCalMonth(pickupDate.startOf("month"));
    if (side === "dropoff" && dropDate)   setCalMonth(dropDate.startOf("month"));
    setStep((s) => (s === `${side}-date` ? null : `${side}-date`));
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {open && ReactDOM.createPortal(
        <ToastContainer
          position="top-center"
          autoClose={4000}
          containerId="overlay-toast"
          style={{ zIndex: 999999 }}
        />,
        document.body
      )}
    {/* Backdrop — portaled to body, plain conditional render, no animation */}
    {open && ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-6 bg-black/55 backdrop-blur-[10px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
        <div className="flex items-start justify-center gap-4 w-full max-w-[1125px]">

            {/* ── LEFT: main form card (hidden in minimal mode) ────────────── */}
            {!minimal && <div
              className="relative bg-white min-[450px]:rounded-3xl shadow-2xl w-full max-w-[550px] shrink-0 overflow-y-auto [&::-webkit-scrollbar]:hidden min-[450px]:max-h-[80vh] max-[449px]:fixed max-[449px]:inset-0 max-[449px]:max-w-none max-[449px]:rounded-none max-[449px]:h-full max-[449px]:max-h-full"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button — mobile only */}
              <button
                onClick={onClose}
                className="max-[449px]:flex hidden absolute top-4 right-4 z-10 w-9 h-9 items-center justify-center rounded-full bg-[#f3f4f6] text-[#374151]"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>

              <div className="p-6 flex flex-col gap-5">

                {/* Date & Time */}
                <div>
                  <p className="mb-2 text-left" style={{ color: "#64748B", fontFamily: "Outfit", fontSize: 13, fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>DATE & TIME</p>
                  <div className="grid gap-3 grid-cols-2">

                    {/* Pick-up card */}
                    <DateCard
                      label="PICK-UP"
                      name="pickup"
                      date={pickupDate}
                      time={pickupTime}
                      done={pickupDone}
                      onClick={() => openPanel("pickup")}
                    />

                    {/* Drop-off card */}
                    <DateCard
                      label="DROP-OFF"
                      name="drop"
                      date={dropDate}
                      time={dropTime}
                      done={dropDone}
                      onClick={() => openPanel("dropoff")}
                    />
                  </div>
                </div>

                {/* Trip Duration + Search button — one joined card */}
                <div className="rounded-2xl overflow-hidden border border-[#e5e7eb] bg-white pt-[6px] pb-[16px] px-[8px]">
                  {pickupDone && dropDone && (
                    <div className="flex items-center gap-3 bg-white px-4 py-1.5">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: PURPLE_LIGHT_BG }}>
                        <Clock size={13} style={{ color: PURPLE_SOLID }} />
                      </div>
                      <span className="text-sm font-semibold font-['Outfit'] text-[#1e1b4b]">
                        Trip Duration: {getDuration()}
                      </span>
                    </div>
                  )}

                  <div className="p-[6px] bg-white">
                  <button
                    onClick={handleSearch}
                    className="w-full h-14 flex items-center justify-between px-6 text-white font-bold font-['Outfit'] text-base transition-opacity hover:opacity-90 active:opacity-80"
                    style={{ background: PURPLE_GRADIENT, borderRadius: 8, boxShadow: PURPLE_SHADOW }}
                  >
                    <span>Search Available Cars</span>
                    <CarSearchIcon />
                  </button>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center gap-1.5">
                  <ShieldInfoIcon />
                  <span style={{ color: "#64748B", fontFamily: "Outfit", fontSize: 13, fontWeight: 500, lineHeight: "normal" }}>
                    Secure booking. No hidden charges.
                  </span>
                </div>
              </div>

              {/* ── Mobile step panel (inside card as overlay) ─────────────── */}
                {step && (
                  <div
                    key={step}
                    className="min-[450px]:hidden absolute inset-0 bg-white z-20 flex flex-col overflow-y-auto [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                  >
                    {/* Back button */}
                    <div className="flex items-center gap-3 px-5 pt-5 pb-3 shrink-0">
                      <button
                        onClick={() => {
                          if (isTimeStep) setStep(`${isPickupStep ? "pickup" : "dropoff"}-date`);
                          else setStep(null);
                        }}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-[#f3f4f6] text-[#374151]"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <span className="font-semibold text-[#0f172a] font-['Outfit'] text-base">
                        {stepLabel}
                      </span>
                    </div>
                    <div className="flex-1 overflow-y-auto px-5 pb-6 flex flex-col gap-4 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>

                      {/* Calendar — only on date step */}
                      {isDateStep && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-[#0f172a] font-['Outfit']">{calMonth.format("MMMM YYYY")}</span>
                            <div className="flex gap-1">
                              <button onClick={() => setCalMonth((m) => m.subtract(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                                <ChevronLeft size={15} className="text-[#6b7280]" />
                              </button>
                              <button onClick={() => setCalMonth((m) => m.add(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                                <ChevronRight size={15} className="text-[#6b7280]" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-7 mb-1">
                            {DAY_LABELS.map((d) => (
                              <div key={d} className="text-center text-[11px] text-[#9ca3af] font-['Outfit'] py-1">{d}</div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-y-0.5">
                            {calDays.map((day, i) => {
                              if (!day) return <div key={i} className="h-9" />;
                              const isPU    = pickupDate && day.isSame(pickupDate, "day");
                              const isDO    = dropDate   && day.isSame(dropDate, "day");
                              const inRange = pickupDate && dropDate && day.isAfter(pickupDate, "day") && day.isBefore(dropDate, "day");
                              const disabled = isDayDisabled(day);
                              let bg = "transparent", color = disabled ? "#d1d5db" : "#374151";
                              if (isPickupStep) {
                                if (isPU) { bg = PURPLE_GRADIENT; color = "white"; }
                              } else {
                                if (isPU)         { bg = PURPLE_GRADIENT; color = "white"; }
                                else if (isDO)    { bg = "#1e1b4b";       color = "white"; }
                                else if (inRange) { bg = PURPLE_LIGHT_BG; color = PURPLE_SOLID; }
                              }
                              return (
                                <button
                                  key={i}
                                  onClick={() => { if (!disabled) handleDayClick(day); }}
                                  className="h-9 w-9 mx-auto rounded-full text-sm font-['Outfit'] flex items-center justify-center transition-all"
                                  style={{ background: bg, color, fontWeight: (isPU || isDO) ? 600 : 400, cursor: disabled ? "not-allowed" : "pointer", boxShadow: isPU ? PURPLE_SHADOW : "none" }}
                                >
                                  {day.date()}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Time grid — only on time step */}
                      {isTimeStep && (
                        <div>
                          <div className="grid grid-cols-4 gap-2">
                            {(isPickupStep ? availPickup : availDrop).map((slot) => {
                              const isActive = isPickupStep ? slot.value === pickupTime : slot.value === dropTime;
                              let btnBg = "white", btnColor = "#374151", btnBorder = "#e5e7eb", btnShadow = "none";
                              if (isActive) {
                                if (isPickupStep) { btnBg = PURPLE_GRADIENT; btnColor = "white"; btnBorder = "transparent"; btnShadow = PURPLE_SHADOW; }
                                else              { btnBg = "#1e1b4b";       btnColor = "white"; btnBorder = "transparent"; }
                              }
                              return (
                                <button
                                  key={slot.value}
                                  onClick={() => handleSlotClick(slot.value)}
                                  className="w-full py-2 rounded-xl text-[11px] font-['Outfit'] border transition-all"
                                  style={{ background: btnBg, color: btnColor, borderColor: btnBorder, boxShadow: btnShadow }}
                                >
                                  {slot.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Duration pill — shown when in dropoff step with both dates set */}
                      {isDropoffStep && pickupDone && dropDone && getDuration() && (
                        <div className="flex items-center gap-2 bg-[#EDE8FC] rounded-xl px-4 py-2.5">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: PURPLE_LIGHT_BG }}>
                            <Clock size={12} style={{ color: PURPLE_SOLID }} />
                          </div>
                          <span className="text-sm font-semibold font-['Outfit'] text-[#1e1b4b]">
                            Trip Duration: {getDuration()}
                          </span>
                        </div>
                      )}

                      {/* Confirm button */}
                      <button
                        onClick={() => closeStep()}
                        className="w-full h-12 flex items-center justify-center text-white font-bold font-['Outfit'] text-sm rounded-xl mt-2"
                        style={{ background: PURPLE_GRADIENT, boxShadow: PURPLE_SHADOW }}
                      >
                        {isDropoffStep ? "Confirm & Search" : `Confirm ${stepTitle}`}
                      </button>
                    </div>
                  </div>
                )}
            </div>}

            {/* ── RIGHT: Date or Time panel (desktop only) ─────────────────── */}
              {step && (
                <div
                  key={step}
                  className={`${minimal ? "flex" : "hidden min-[450px]:flex"} bg-white rounded-3xl shadow-2xl flex-col gap-4 p-5 overflow-y-auto shrink-0 w-full max-w-[525px] [&::-webkit-scrollbar]:hidden`}
                  style={{ maxHeight: "90vh", scrollbarWidth: "none", msOverflowStyle: "none" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Panel header */}
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#0f172a] font-['Outfit'] text-base">
                      {stepLabel}
                    </span>
                    <button
                      onClick={() => {
                        if (isTimeStep) setStep(`${isPickupStep ? "pickup" : "dropoff"}-date`);
                        else if (minimal) onClose();
                        else setStep(null);
                      }}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f3f4f6] text-[#374151]"
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                        {isTimeStep
                          ? <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          : <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        }
                      </svg>
                    </button>
                  </div>

                  {/* Calendar — date step only */}
                  {isDateStep && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-[#0f172a] font-['Outfit']">{calMonth.format("MMMM YYYY")}</span>
                        <div className="flex gap-1">
                          <button onClick={() => setCalMonth((m) => m.subtract(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                            <ChevronLeft size={15} className="text-[#6b7280]" />
                          </button>
                          <button onClick={() => setCalMonth((m) => m.add(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                            <ChevronRight size={15} className="text-[#6b7280]" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-7 mb-1">
                        {DAY_LABELS.map((d) => (
                          <div key={d} className="text-center text-[11px] text-[#9ca3af] font-['Outfit'] py-1">{d}</div>
                        ))}
                      </div>

                      <div className="grid grid-cols-7 gap-y-0.5">
                        {calDays.map((day, i) => {
                          if (!day) return <div key={i} className="h-9" />;
                          const isPU    = pickupDate && day.isSame(pickupDate, "day");
                          const isDO    = dropDate   && day.isSame(dropDate, "day");
                          const inRange = pickupDate && dropDate && day.isAfter(pickupDate, "day") && day.isBefore(dropDate, "day");
                          const disabled = isDayDisabled(day);

                          let bg = "transparent", color = disabled ? "#d1d5db" : "#374151";
                          if (isPickupStep) {
                            if (isPU) { bg = PURPLE_GRADIENT; color = "white"; }
                          } else {
                            if (isPU)         { bg = PURPLE_GRADIENT; color = "white"; }
                            else if (isDO)    { bg = "#1e1b4b";       color = "white"; }
                            else if (inRange) { bg = PURPLE_LIGHT_BG; color = PURPLE_SOLID; }
                          }

                          return (
                            <button
                              key={i}
                              onClick={() => { if (!disabled) handleDayClick(day); }}
                              className="h-9 w-9 mx-auto rounded-full text-sm font-['Outfit'] flex items-center justify-center transition-all"
                              style={{
                                background: bg,
                                color,
                                fontWeight: (isPU || isDO) ? 600 : 400,
                                cursor: disabled ? "not-allowed" : "pointer",
                                boxShadow: isPU ? PURPLE_SHADOW : "none",
                              }}
                            >
                              {day.date()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Time grid — time step only */}
                  {isTimeStep && (
                    <div>
                      <div className="grid grid-cols-4 gap-2">
                        {(isPickupStep ? availPickup : availDrop).map((slot) => {
                          const isActive = isPickupStep ? slot.value === pickupTime : slot.value === dropTime;
                          let btnBg = "white", btnColor = "#374151", btnBorder = "#e5e7eb", btnShadow = "none";
                          if (isActive) {
                            if (isPickupStep) { btnBg = PURPLE_GRADIENT; btnColor = "white"; btnBorder = "transparent"; btnShadow = PURPLE_SHADOW; }
                            else              { btnBg = "#1e1b4b";       btnColor = "white"; btnBorder = "transparent"; }
                          }
                          return (
                            <button
                              key={slot.value}
                              onClick={() => handleSlotClick(slot.value)}
                              className="w-full py-2 rounded-xl text-[11px] font-['Outfit'] border transition-all"
                              style={{ background: btnBg, color: btnColor, borderColor: btnBorder, boxShadow: btnShadow }}
                            >
                              {slot.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Duration pill — shown when in dropoff step with both dates set */}
                  {isDropoffStep && pickupDone && dropDone && getDuration() && (
                    <div className="flex items-center gap-2 bg-[#EDE8FC] rounded-xl px-4 py-2.5">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: PURPLE_LIGHT_BG }}>
                        <Clock size={12} style={{ color: PURPLE_SOLID }} />
                      </div>
                      <span className="text-sm font-semibold font-['Outfit'] text-[#1e1b4b]">
                        Trip Duration: {getDuration()}
                      </span>
                    </div>
                  )}

                  {/* Confirm / close button */}
                  <button
                    onClick={() => closeStep()}
                    className="w-full h-12 flex items-center justify-center text-white font-bold font-['Outfit'] text-sm rounded-xl mt-2"
                    style={{ background: PURPLE_GRADIENT, boxShadow: PURPLE_SHADOW }}
                  >
                    {isDropoffStep ? "Confirm & Search" : `Confirm ${stepTitle}`}
                  </button>
                </div>
              )}

        </div>
    </div>,
    document.body
    )}
    </>
  );
}
